import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import { getMoviesByCategory, movieCategories } from "@/mocks/movies";

const subCategories: Record<string, string[]> = {
  dianying: ["全部", "科幻", "喜剧", "动作", "奇幻", "悬疑"],
  dianshiju: ["全部", "都市", "科幻", "古装", "悬疑", "爱情"],
  dongman: ["全部", "热血", "战斗", "奇幻", "超级英雄", "日常", "治愈"],
  zongyi: ["全部", "竞演", "生活", "户外", "音乐", "游戏"],
};

const categoryDescriptions: Record<string, string> = {
  dianying: "蓝莓影院电影频道汇聚海量高清电影资源，涵盖科幻巨制、喜剧大片、动作猛片、奇幻史诗、悬疑佳片等多个类型。无论你喜欢好莱坞大片还是国产佳作，这里都能满足你的观影需求。",
  dianshiju: "蓝莓影院电视剧频道提供全网最新最热的电视剧资源，从都市情感、科幻悬疑到古装权谋、现代爱情，热门好剧每日更新，追剧无忧，高清流畅播放体验。",
  dongman: "蓝莓影院动漫频道涵盖热血战斗、奇幻冒险、超级英雄、日常治愈等全品类动漫作品。日本新番同步更新，国产动漫持续上线，二次元的世界等你来探索。",
  zongyi: "蓝莓影院综艺频道汇集音乐竞演、户外真人秀、生活慢综、游戏对决等各类热门综艺节目。每周更新精彩内容，陪伴你的休闲时光。",
};

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSub, setActiveSub] = useState("全部");
  const [sortBy, setSortBy] = useState<"default" | "rating" | "newest">("default");

  const category = movieCategories.find((c) => c.slug === slug);
  const allMovies = getMoviesByCategory(slug || "dianying");
  const subs = subCategories[slug || "dianying"] || ["全部"];

  useEffect(() => {
    setActiveSub("全部");
    setSortBy("default");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  let filteredMovies =
    activeSub === "全部"
      ? allMovies
      : allMovies.filter((m) => m.subCategory === activeSub);

  if (sortBy === "rating") {
    filteredMovies = [...filteredMovies].sort((a, b) => b.rating - a.rating);
  } else if (sortBy === "newest") {
    filteredMovies = [...filteredMovies].sort(
      (a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()
    );
  }

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${category?.name || "影视"} - 蓝莓影院 | 高清免费在线观看`,
    description: categoryDescriptions[slug || "dianying"] || `蓝莓影院${category?.name || "影视"}分类，高清免费在线观看，海量${category?.name || "影视"}资源每日更新，流畅不缓冲即点即看。`,
    url: window.location.href,
    isPartOf: { "@id": `${window.location.origin}/#website` },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "首页",
          item: window.location.origin,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: category?.name || "影视",
        },
      ],
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <Breadcrumb items={[{ label: category?.name || "影视" }]} />

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 pb-10 md:pb-14">
        {/* 页面头部 */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
            {category?.name || "影视"}
          </h1>
          <p className="text-sm text-foreground-500 mt-2 max-w-3xl">
            {categoryDescriptions[slug || "dianying"] ||
              `高清免费${category?.name || "影视"}在线观看，每日更新精彩内容`}
          </p>
        </div>

        {/* 过滤栏 */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            {subs.map((sub) => (
              <button
                key={sub}
                onClick={() => {
                  setActiveSub(sub);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  activeSub === sub
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {sub}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
            {[
              { key: "default" as const, label: "默认" },
              { key: "rating" as const, label: "评分最高" },
              { key: "newest" as const, label: "最新更新" },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  sortBy === opt.key
                    ? "bg-secondary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* 过滤结果统计 */}
        <p className="text-xs text-foreground-500 mb-4">
          共找到 <strong className="text-foreground-800">{filteredMovies.length}</strong> 部
          {activeSub !== "全部" ? `「${activeSub}」` : ""}
          相关作品
        </p>

        {/* 影片列表 */}
        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5"
          data-product-shop
        >
          {filteredMovies.map((movie) => (
            <MovieCard
              key={movie.id}
              id={movie.id}
              title={movie.title}
              image={movie.image}
              rating={movie.rating}
              category={movie.category}
              year={movie.year}
              isNew={movie.isNew}
            />
          ))}
        </div>

        {filteredMovies.length === 0 && (
          <div className="text-center py-20">
            <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-film-line text-foreground-400 text-2xl"></i>
            </span>
            <p className="text-foreground-500 text-sm">暂无该分类的影片</p>
            <p className="text-foreground-400 text-xs mt-1">试试切换其他筛选条件</p>
          </div>
        )}
      </section>
    </>
  );
}